import React, { useEffect, useState } from "react";
import './PagoAliados.scss';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FileUpload } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getProveedores, getSubirExel, subirAoddo } from "./integracion";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import {InputIcon} from "primereact/inputicon";
import {IconField} from "primereact/iconfield";

const PagoAliados = () => {
    const [archivoCargado, setArchivoCargado] = useState(null);
    const [proveedores, setProveedores] = useState([]);
    const [proveedor, setProveedor] = useState(null);
    const [dataTableExel, setDataTableExel] = useState({ data: [] });
    const [dataTableSuma, setDataTableSuma] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);

    // Calcular totales
    const calcularTotales = (data) => {
        if (!data || data.length === 0) {
            return { total: 0, cantidad: 0 };
        }
        return data.reduce((acc, item) => ({
            total: acc.total + (parseFloat(item.total) || 0),
            cantidad: acc.cantidad + (parseFloat(item.cantidad) || 0)
        }), { total: 0, cantidad: 0 });
    };

    const totalesExcel = calcularTotales(dataTableExel.data);
    const totalesSuma = calcularTotales(dataTableSuma);
    const totalesCoinciden = totalesExcel.total === totalesSuma.total && dataTableExel.data.length > 0;

    useEffect(() => {
        getProveedores().then((data) => setProveedores(data));
    }, []);

    // Formatear moneda
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value || 0);
    };

    const monedaTemplate = (rowData, field) => formatCurrency(rowData[field]);

    const cantidadTemplate = (rowData) => (
        <span className="font-semibold">{rowData.cantidad}</span>
    );

    const onUploadExcel = (event) => {
        setArchivoCargado(event.files[0]);
        Swal.fire({
            icon: "success",
            title: "Archivo cargado",
            text: `${event.files[0].name}`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    const onComparateData = () => {
        if (archivoCargado && proveedor) {
            setLoading(true);
            getSubirExel(archivoCargado, proveedor.code).then((data) => {
                setDataTableExel(data.dataExcel);
                setDataTableSuma(data.dataSuma);
                setLoading(false);
            });
        } else {
            Swal.fire({
                icon: "error",
                title: "Campos requeridos",
                text: "Seleccione proveedor y archivo Excel",
            });
        }
    };

    const subirAoddof = () => {
        if (dataTableExel.data.length === 0) {
            Swal.fire({ icon: "warning", text: "Comparar datos primero" });
            return;
        }

        if (!totalesCoinciden) {
            Swal.fire({
                icon: "error",
                title: "Los totales no coinciden",
                html: `
                    <div style="text-align: left; margin: 1rem 0;">
                        <p><strong>Excel:</strong> ${formatCurrency(totalesExcel.total)}</p>
                        <p><strong>Sistema:</strong> ${formatCurrency(totalesSuma.total)}</p>
                        <p><strong>Diferencia:</strong> ${formatCurrency(Math.abs(totalesExcel.total - totalesSuma.total))}</p>
                    </div>
                `,
            });
            return;
        }

        Swal.fire({
            title: 'Confirmación',
            text: '¿Subir datos a Odoo?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí',
            cancelButtonText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                subirAoddo(proveedor.code, dataTableExel.fecha_inicio, dataTableExel.fecha_fin).then(() => {
                    setDataTableExel({ data: [] });
                    setDataTableSuma([]);
                    setLoading(false);
                    Swal.fire({ icon: "success", title: "Datos subidos", timer: 1500, showConfirmButton: false });
                });
            }
        });
    };

    const descargarExcel = () => {
        if (dataTableSuma.length === 0) return;
        const ws = XLSX.utils.json_to_sheet(dataTableSuma);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Datos");
        XLSX.writeFile(wb, `${proveedor?.name || 'datos'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="PagoAliados p-fluid">
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            {/* CONFIGURACIÓN COMPACTA */}
            <Card className="config-card">
                <div className="card-header">
                    <Tag value="Paso 1" severity="info" />
                    <h2>Configuración</h2>
                </div>

                <div className="grid">
                    <div className="col-12 md:col-5">
                        <label className="field-label">
                            Proveedor <span className="required">*</span>
                        </label>
                        <Dropdown
                            value={proveedor}
                            options={proveedores}
                            onChange={(e) => setProveedor(e.value)}
                            optionLabel="name"
                            placeholder="Seleccione proveedor"
                            filter
                            className="w-full"
                        />
                    </div>

                    <div className="col-12 md:col-5">
                        <label className="field-label">
                            Archivo Excel <span className="required">*</span>
                        </label>
                        <FileUpload
                            mode="basic"
                            accept=".xlsx,.xls"
                            maxFileSize={5000000}
                            onSelect={onUploadExcel}
                            chooseLabel={archivoCargado?.name || "Seleccionar Excel"}
                            className="w-full"
                            auto
                        />
                    </div>

                    <div className="col-12 md:col-2 flex align-items-end">
                        <Button
                            label="Comparar"
                            icon="pi pi-refresh"
                            onClick={onComparateData}
                            disabled={!proveedor || !archivoCargado || loading}
                            className="w-full"
                        />
                    </div>
                </div>

                {dataTableExel.proveedor && (
                    <div className="info-bar mt-2">
                        <div className="info-item">
                            <strong>Proveedor:</strong> {dataTableExel.proveedor}
                        </div>
                        <div className="info-item">
                            <i className="pi pi-calendar"></i> {dataTableExel.fecha_inicio} - {dataTableExel.fecha_fin}
                        </div>
                    </div>
                )}
            </Card>

            {/* COMPARACIÓN */}
            {dataTableExel.data.length > 0 && (
                <Card className="comparison-card mt-2">
                    <div className="card-header">
                        <Tag value="Paso 2" severity="success" />
                        <h2>Comparación</h2>
                        {totalesCoinciden ? (
                            <Tag icon="pi pi-check" severity="success" value="Coinciden" />
                        ) : (
                            <Tag icon="pi pi-times" severity="danger" value="No coinciden" />
                        )}
                    </div>

                    {/* Búsqueda */}
                    <div className="mb-2">
                        <IconField iconPosition="left">
                            <InputIcon className="pi pi-search"> </InputIcon>
                            <InputText
                                placeholder="Buscar..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                            />
                        </IconField>
                    </div>

                    <div className="grid">
                        {/* EXCEL */}
                        <div className="col-12 lg:col-6">
                            <div className="table-container excel-container">
                                <div className="table-header-compact">
                                    <div className="title">
                                        <i className="pi pi-file-excel"></i>
                                        <span>Excel</span>
                                    </div>
                                    <div className="stats-compact">
                                        <span><strong>{formatCurrency(totalesExcel.total)}</strong></span>
                                        <span>{totalesExcel.cantidad} servicios</span>
                                        <span>{dataTableExel.data.length} registros</span>
                                    </div>
                                </div>

                                <DataTable
                                    value={dataTableExel.data.filter((item) =>
                                        Object.values(item).some(val =>
                                            val?.toString().toLowerCase().includes(globalFilter.toLowerCase())
                                        )
                                    )}
                                    scrollable
                                    scrollHeight="400px"
                                    size="small"
                                    stripedRows
                                    emptyMessage="Sin datos"
                                >
                                    <Column field="cliente" header="Cliente" sortable style={{ minWidth: '140px' }} />
                                    <Column field="precio" header="Precio" body={(r) => monedaTemplate(r, 'precio')} sortable style={{ minWidth: '100px' }} />
                                    <Column field="ruta" header="Ruta" sortable style={{ minWidth: '80px' }} />
                                    <Column field="tipo_unidad" header="Unidad" sortable style={{ minWidth: '90px' }} />
                                    <Column field="cantidad" header="Cant." body={cantidadTemplate} sortable style={{ minWidth: '80px' }} />
                                    <Column field="total" header="Total" body={(r) => monedaTemplate(r, 'total')} sortable style={{ minWidth: '100px' }} />
                                </DataTable>
                            </div>
                        </div>

                        {/* SISTEMA */}
                        <div className="col-12 lg:col-6">
                            <div className="table-container suma-container">
                                <div className="table-header-compact">
                                    <div className="title">
                                        <i className="pi pi-database"></i>
                                        <span>Sistema</span>
                                    </div>
                                    <div className="stats-compact">
                                        <span><strong>{formatCurrency(totalesSuma.total)}</strong></span>
                                        <span>{totalesSuma.cantidad} servicios</span>
                                        <span>{dataTableSuma.length} registros</span>
                                        {dataTableSuma.length > 0 && (
                                            <Button
                                                icon="pi pi-download"
                                                onClick={descargarExcel}
                                                text
                                                size="small"
                                                tooltip="Descargar"
                                            />
                                        )}
                                    </div>
                                </div>

                                <DataTable
                                    value={dataTableSuma.filter((item) =>
                                        Object.values(item).some(val =>
                                            val?.toString().toLowerCase().includes(globalFilter.toLowerCase())
                                        )
                                    )}
                                    scrollable
                                    scrollHeight="400px"
                                    size="small"
                                    stripedRows
                                    emptyMessage="Sin datos"
                                >
                                    <Column field="cliente" header="Cliente" sortable style={{ minWidth: '140px' }} />
                                    <Column field="precio" header="Precio" body={(r) => monedaTemplate(r, 'precio')} sortable style={{ minWidth: '100px' }} />
                                    <Column field="ruta" header="Ruta" sortable style={{ minWidth: '80px' }} />
                                    <Column field="tipo_unidad" header="Unidad" sortable style={{ minWidth: '90px' }} />
                                    <Column field="cantidad" header="Cant." body={cantidadTemplate} sortable style={{ minWidth: '80px' }} />
                                    <Column field="total" header="Total" body={(r) => monedaTemplate(r, 'total')} sortable style={{ minWidth: '100px' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    {/* Acción */}
                    <Divider />
                    <div className="action-bar">
                        {!totalesCoinciden && (
                            <div className="error-message">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>Diferencia: {formatCurrency(Math.abs(totalesExcel.total - totalesSuma.total))}</span>
                            </div>
                        )}
                        <Button
                            label="Subir a Odoo"
                            icon="pi pi-upload"
                            onClick={subirAoddof}
                            disabled={!totalesCoinciden || loading}
                            severity={totalesCoinciden ? "success" : "secondary"}
                            size="large"
                        />
                    </div>
                </Card>
            )}
        </div>
    );
};

export default PagoAliados;
